import { UploadZoneRoot } from '@components/FileUpload/UploadZoneRoot';
import { expenseStatusMessages } from '@modules/budget/messages';
import { DocumentFileNameAndIcon } from '@modules/documents/components/DocumentFilenameAndIcon';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Delete, UploadFile } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogProps, FormLabel, IconButton, Link, ListItem, MenuItem, Stack, TextField, Typography, FormHelperText, FormControl, Autocomplete } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { useNotification } from '@utils/useNotification';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { useValidationRules } from '@utils/useValidationRules';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import dayjs, { Dayjs } from 'dayjs';
import { ExpenseFragmentFragment, ExpenseStatus, useAddExpenseMutation, useBudgetItemsQuery, useDeleteExpenseAttachmentMutation, useEditExpenseMutation, useExpensesQuery, useGetProjectBudgetQuery } from 'gql/index';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useExpenseAttachmentDropzone } from './useExpenseAttachmentDropzone';

const noCostItemId = '';
const noQuoteId = '';
type NoCostItemId = typeof noCostItemId;
type NoQuoteId = typeof noQuoteId;

interface FormValues {
  name: string;
  expendedAmount: string;
  expendedDate: Dayjs;
  status: ExpenseStatus;
  costItemId: number | NoCostItemId;
  quoteId: number | NoQuoteId;
}

const defaultValues: FormValues = {
  name: '',
  expendedAmount: '',
  expendedDate: dayjs(),
  status: ExpenseStatus.NotPaid,
  costItemId: noCostItemId,
  quoteId: noQuoteId
};

interface Props extends DialogProps {
  expense?: ExpenseFragmentFragment;
  costItemId?: number;
  quoteId?: number;
  entryTypeSwitcher?: ReactNode;
}

type RequestSender = (expenseId: number, onComplete: () => void) => void;

export const ExpenseDialog: React.FC<Props> = ({ expense, costItemId, quoteId: initialQuoteId, entryTypeSwitcher, ...props }) => {
  const { notEmpty, validateNumber, validateIsPositive } = useValidationRules();
  const { formatMessage } = useIntl();
  const { projectId } = useCurrentProject();
  const invalidateQuery = useQueryInvalidator();
  const { notifySuccess } = useNotification();

  const [currentAttachment, setCurrentAttachment] = useState<{ id?: number; fileName: string } | null>(null);
  const [fileSender, setFileSender] = useState<RequestSender | null>(null);

  const { reset, control, handleSubmit } = useForm<FormValues>({ defaultValues });
  const costItemIdWatched = useWatch({ control, name: 'costItemId', defaultValue: noCostItemId });

  const { data: budgetItems } = useBudgetItemsQuery({ projectId }, { select: d => d.budgetItems });

  useEffect(() => {
    if (expense) {
      reset({
        name: expense.name,
        expendedAmount: expense.expendedAmount.toString(),
        expendedDate: dayjs(expense.expenseDate),
        status: expense.status,
        costItemId: costItemId ?? noCostItemId,
        quoteId: expense.quoteId != null ? expense.quoteId : noQuoteId
      });
      if (expense.attachments?.length) {
        const attachment = expense.attachments[0];
        setCurrentAttachment({ id: attachment.id, fileName: attachment.fileName });
      } else {
        setCurrentAttachment(null);
      }
      setFileSender(null);
      setIsUploadingFile(false);
    } else {
      reset({
        ...defaultValues,
        costItemId: costItemId ?? noCostItemId,
        quoteId: initialQuoteId != null ? initialQuoteId : noQuoteId
      });
      setCurrentAttachment(null);
      setFileSender(null);
      setIsUploadingFile(false);
    }
  }, [expense, reset, props.open, costItemId, initialQuoteId]);


  const onSuccess = () => {
    invalidateQuery(useExpensesQuery, { projectId });
    invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
    invalidateQuery(useBudgetItemsQuery, { projectId });
    notifySuccess(formatMessage({ id: 'Expense updated successfully.' }));
    props.onClose?.({}, 'backdropClick');
    setIsUploadingFile(false);
  };

  const { mutateAsync: addExpense, isPending: isAdding } = useAddExpenseMutation();

  const { mutateAsync: editExpense, isPending: isEditing } = useEditExpenseMutation();

  const { mutateAsync: deleteExpenseAttachment, isPending: isDeletingExpenseAttachment } = useDeleteExpenseAttachmentMutation();

  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const isLoading = isAdding || isEditing || isUploadingFile || isDeletingExpenseAttachment;

  const onSubmit = async (data: FormValues) => {
    const resolvedCostItemId = data.costItemId === noCostItemId ? undefined : Number(data.costItemId);
    if (resolvedCostItemId == null || Number.isNaN(resolvedCostItemId)) {
      return;
    }

    let expenseId = expense?.id;
    const resolvedQuoteId = typeof data.quoteId === 'number' ? data.quoteId : undefined;

    if (expense) {
      await editExpense({
        input: {
          id: expense.id,
          name: data.name,
          expendedAmount: Number(data.expendedAmount),
          expenseDate: data.expendedDate.toISOString(),
          status: data.status,
          costItemId: resolvedCostItemId,
          projectId,
          quoteId: resolvedQuoteId ?? null
        }
      });

    } else {
      const result = await addExpense({
        input: {
          name: data.name,
          expendedAmount: Number(data.expendedAmount),
          expenseDate: data.expendedDate.toISOString(),
          status: data.status,
          costItemId: resolvedCostItemId,
          projectId,
          quoteId: resolvedQuoteId ?? null
        }
      });

      expenseId = result.addExpense.expense?.id;
    }



    if (!expenseId) {
      return;
    }

    if (fileSender) {
      setIsUploadingFile(true);

      fileSender(expenseId, () => {
        setCurrentAttachment(null);
        setFileSender(null);
        onSuccess();
      });
    } else {
      const didUserRemoveFile = !currentAttachment && (expense?.attachments?.length ?? 0) > 0;

      if (didUserRemoveFile) {
        await deleteExpenseAttachment({ input: { expenseId, projectId } });
        onSuccess();
      }
      else {
        onSuccess();
      }
    }
  };

  const { getInputProps, getRootProps, isDragActive } = useExpenseAttachmentDropzone({
    disabled: isLoading,
    onFileUploadReady: (sender, filename) => {
      setCurrentAttachment({ fileName: filename });
      setFileSender(() => sender);
    }
  });

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      {...props}
    >
      <DialogTitle>
        <Stack gap={1}>
          <Typography variant='h6'>
            {expense ? formatMessage({ id: 'Edit Expense' }) : formatMessage({ id: 'Add Expense' })}
          </Typography>
          {entryTypeSwitcher}
        </Stack>
      </DialogTitle>
      <DialogContent>
      <Stack pt={1} pb={1} gap={2}>
        <Controller
          name='name'
          control={control}
          rules={{ validate: { notEmpty } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              required
              label={formatMessage({ id: 'Name' })}
              error={!!error}
              helperText={error?.message}
              disabled={isLoading}
            />
          )}
        />

        <Controller
          name='expendedAmount'
          control={control}
          rules={{ validate: { notEmpty, validateNumber, validateIsPositive } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={formatMessage({ id: 'Expense Amount' })}
              required
              error={!!error}
              helperText={error?.message}
              disabled={isLoading}
            />
          )}
        />

        <Controller
          name='expendedDate'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DesktopDatePicker
              {...field}
              label={formatMessage({ id: 'Expense Date' })}
              disabled={isLoading}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: error?.message
                }
              }}
            />
          )}
        />

        <Controller
          name='status'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField select
              {...field}
              label={formatMessage({ id: 'Status' })}
              error={!!error}
              helperText={error?.message}
              disabled={isLoading}
            >
              {Object.values(ExpenseStatus).map(status => (
                <MenuItem key={status} value={status}>
                  {formatMessage(expenseStatusMessages[status])}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          name='costItemId'
          control={control}
          rules={{ validate: { notEmpty } }}
          render={({ field, fieldState: { error } }) => {
            const costItemOptions = useMemo(() => {
              const items = budgetItems ?? [];
              const sortedBudgetItems = [...items].sort((a, b) => a.name.localeCompare(b.name));
              return sortedBudgetItems.flatMap(budgetItem => {
                const costItems = [...(budgetItem.costItems ?? [])].sort((a, b) => a.name.localeCompare(b.name));
                return costItems.map(costItem => ({
                  id: costItem.id,
                  name: costItem.name,
                  group: budgetItem.name
                }));
              });
            }, [budgetItems]);

            const selectedOption = costItemOptions.find(o => o.id === field.value) ?? null;

            return (
              
               <FormControl sx={{ mb: 1 }}>
              <Autocomplete
               id='cost-item'
               disableClearable
               options={costItemOptions}
               groupBy={(option) => option.group}
               getOptionLabel={(option) => option.name}
               isOptionEqualToValue={(option, value) => option.id === value.id}
               value={selectedOption ?? undefined}
               onChange={(_, option) => field.onChange(option?.id ?? noCostItemId)}
               onBlur={field.onBlur}
               disabled={isLoading}
               renderInput={(params) => (
                 <TextField
                   {...params}
                   label={formatMessage({ id: 'Cost item' })}
                   required
                   error={!!error}
                   helperText={error?.message}
                 />
               )}
              />
              {!selectedOption && <FormHelperText error={!!error}>{error?.message}</FormHelperText>}
              </FormControl>
            );
          }} />

        <Controller
          name='quoteId'
          control={control}
          render={({ field, fieldState: { error } }) => {
            const resolvedCostItemId = costItemIdWatched === noCostItemId ? null : Number(costItemIdWatched);
            const quoteOptions = useMemo(() => {
              if (resolvedCostItemId == null || Number.isNaN(resolvedCostItemId)) return [];
              return (budgetItems ?? []).flatMap(bi => (bi.costItems ?? []))
                .filter(ci => ci.id === resolvedCostItemId)
                .flatMap(ci => (ci.entries ?? []).filter(e => e.quote != null).map(e => ({ id: e.quote!.id, name: e.quote!.name })));
            }, [budgetItems, resolvedCostItemId]);
            const selectedQuote = quoteOptions.find(o => o.id === field.value) ?? null;
            return (
              <FormControl sx={{ mb: 1 }}>
                <Autocomplete
                  id='expense-quote'
                  options={quoteOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedQuote}
                  onChange={(_, option) => field.onChange(option?.id ?? noQuoteId)}
                  onBlur={field.onBlur}
                  disabled={isLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={formatMessage({ id: 'Quote (optional)' })}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FormControl>
            );
          }} />
        <Stack>
          <FormLabel>{formatMessage({ id: 'Invoice' })}</FormLabel>
          {currentAttachment ? (
            <ListItem disablePadding sx={{ border: t => `1px solid ${t.palette.divider}` }}>
              <DocumentFileNameAndIcon
                fileName={currentAttachment.fileName}
                downloadLink={currentAttachment.id ? `/${getTenantIdentifier()}/api/Documents/${currentAttachment.id}` : undefined}

                actionButton={!isLoading && (
                  <IconButton
                    disabled={isLoading}
                    onClick={() => {
                      setCurrentAttachment(null);
                      setFileSender(null);
                    }}
                  >
                    <Delete />
                  </IconButton>
                )}
              />
            </ListItem>
          ) : (
            <UploadZoneRoot isDragActive={isDragActive} dropZoneRootProps={getRootProps()}>
              <UploadFile color='primary' />
              <input {...getInputProps()} />
              <Stack alignItems='center' gap={1}>
                <Typography variant='body1'>
                  <Link href='#'>
                    {formatMessage({ id: 'Click to upload' })}
                  </Link>
                  {' '}{formatMessage({ id: 'or drag and drop' })}
                </Typography>
                <Typography variant='body1' color='grey.600'>{formatMessage({ id: '(10MB max)' })}</Typography>
              </Stack>
            </UploadZoneRoot>
          )}
        </Stack>
      </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={(event) => props.onClose?.(event, 'backdropClick')} disabled={isLoading}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
        <Button variant='contained' onClick={handleSubmit(onSubmit)} disabled={isLoading}>
          {formatMessage({ id: 'Save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};